import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    InputAdornment,
    Link,
    Alert,
    CircularProgress,
} from "@mui/material";

import {
    EmailOutlined,
    LockOutlined,
} from "@mui/icons-material";

import { loginUser } from "../api/authApi";
import { saveAuth, getHomePathForRole } from "../utils/auth";

const Login = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [formData, setFormData] = useState({
        email: "",
        password: ""
    });

    const handleChange = (event) => {
        const { name, value } = event.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setError("");

        if (!formData.email || !formData.password) {
            setError("Please enter your email and password.");
            return;
        }

        try {
            setLoading(true);

            const data = await loginUser({
                email: formData.email,
                password: formData.password,
            });

            saveAuth(data.token, data.user);
            navigate(getHomePathForRole(data.user.role));

        } catch (err) {
            setError(
                err?.response?.data?.message ||
                "Invalid email or password. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                    "linear-gradient(135deg, #eef6ff 0%, #f5fbfa 50%, #ffffff 100%)",
                px: 1,
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    maxWidth: 380,
                    p: {
                        xs: 3,
                        sm: 4,
                    },
                    borderRadius: 3,
                }}
            >
                <Typography
                    variant="h6"
                    fontWeight={600}
                    textAlign="center"
                    gutterBottom
                >
                    Welcome to JobPortal
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{ mb: 1 }}
                >
                    Sign in to access your job search portal
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="email"
                        margin="normal"
                        placeholder="Enter your email"
                        sx={{
                            mb: 0,
                            "& input::placeholder": {
                                fontSize: "14px",
                            },
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start" >
                                        <EmailOutlined sx={{
                                            fontSize: 18,
                                            color: "lightgrey",
                                        }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    <TextField
                        fullWidth
                        size="small"
                        label="Password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        margin="normal"
                        placeholder="Enter your password"
                        sx={{
                            mb: 0,
                            "& input::placeholder": {
                                fontSize: "14px",
                            },
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockOutlined sx={{
                                            fontSize: 18,
                                            color: "lightgrey",
                                        }} />
                                    </InputAdornment>
                                ),
                            }
                        }}
                    />

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mt: 1,
                            mb: 2,
                        }}
                    >
                        <Link
                            href="/forgot-password"
                            underline="hover"
                            variant="body2"
                        >
                            Forgot password?
                        </Link>
                    </Box>

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                            height: 38,
                            borderRadius: 2,
                            textTransform: "none",
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            "Sign In"
                        )}
                    </Button>
                </Box>

                <Typography
                    variant="body2"
                    textAlign="center"
                    color="text.secondary"
                    sx={{ mt: 3 }}
                >
                    Don't have an account?{" "}
                    <Link
                        href="/signup"
                        underline="hover"
                        fontWeight={600}
                    >
                        Create account
                    </Link>
                </Typography>

            </Paper>
        </Box>
    );
};

export default Login;